import { useAdminStore } from '@admin/AppProvider'
import { SingleLocationCodeAutocompleteProps } from '@admin/pages/TenantConfiguration/components/form/tenant-config.interface'
import { Box, Button, Container } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { Header, Loader, useMinimalsTheme } from '@tom-ui/ui'
import { PATHS, useAsyncFetch, useHistory } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router'
import { TenantForm } from '../components/form/TenantForm'
import { TenantFormMapper } from '../components/form/tenant-form.mapper'
import { TenantFormProfile } from '../components/form/tenant-form.profile'

interface Props {
  SingleLocationCodeAutoComplete: React.ComponentType<SingleLocationCodeAutocompleteProps>
}

export const TenantFormPage = observer(({ SingleLocationCodeAutoComplete }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { appStore, tenantStore, tenantUIStore, timezonesStore } = useAdminStore()
  const history = useHistory()
  const navigate = useNavigate()
  const id = useParams<{ id: string }>().id
  const tenantId = id ? Number(id) : undefined

  const { loading } = useAsyncFetch(async () => {
    if (tenantId) {
      await tenantStore.loadOne(tenantId)
      tenantUIStore.setSelectedTenantId(tenantId)
    }
    timezonesStore.load()
  }, [tenantStore, tenantId, timezonesStore])

  useEffect(() => {
    return () => tenantUIStore.setSelectedTenantId()
  }, [])

  const isEdit = !!tenantId

  const handleSubmit = async (formValues: TenantFormProfile) => {
    try {
      if (isEdit) {
        await tenantStore.update(TenantFormMapper.mapFormValuesToUpdateDto(formValues))
        appStore.setShowAlert(
          'success',
          t('tenantUpdatedSuccessfully', 'Tenant updated successfully'),
        )
      } else {
        await tenantStore.add(TenantFormMapper.mapFormValuesToCreateDto(formValues))
        appStore.setShowAlert(
          'success',
          t('tenantCreatedSuccessfully', 'Tenant created successfully'),
        )
      }
      navigate(`${PATHS.config.tenants.url}`)
    } catch (error) {
      appStore.setShowAlert('error', t('somethingWentWrong', 'Something went wrong'))
    }
  }
  const handleClose = () => {
    tenantUIStore.setSelectedTenantId()
    history.goBackHistoryOrDefault(PATHS.config.tenants.url)
  }

  const title = isEdit ? t('editTenant', 'Edit tenant') : t('addNewTenant', 'Add new tenant')

  const breadcrumbs = [
    {
      label: t(PATHS.config.tenants.title),
      route: PATHS.config.tenants.url,
    },
    {
      label: title,
    },
  ]

  return (
    <Box
      sx={{
        bgcolor: theme.palette.grey[100],
        minHeight: '100%',
      }}
    >
      <Header
        title={title}
        position='relative'
        breadcrumbs={breadcrumbs}
        rightRenderOption={
          <>
            <Button size='large' color='inherit' onClick={handleClose} variant='text'>
              {t('cancel', 'Cancel')}
            </Button>
            <Button
              type='submit'
              form='tenant-form'
              size='large'
              color='primary'
              variant='contained'
              data-cy='tenant-form-submit-btn'
            >
              {t('submit', 'Submit')}
            </Button>
          </>
        }
      />
      <Container sx={{ padding: theme.customSpacing.m }}>
        {loading ? (
          <Loader show />
        ) : (
          <TenantForm
            id='tenant-form'
            tenant={tenantUIStore.selectedTenant}
            timezonesStore={timezonesStore}
            onSubmit={handleSubmit}
            SingleLocationCodeAutoComplete={SingleLocationCodeAutoComplete}
          />
        )}
      </Container>
    </Box>
  )
})
